import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

/**
 * Tracks in-flight HTTP requests to drive the single app-wide loading overlay.
 * Reference-counted so overlapping requests keep the overlay up until the last
 * one settles. Driven by {@link LoadingInterceptor}; components only call it to
 * span a multi-step flow (e.g. entering a show from the pick-show screen).
 */
@Injectable({ providedIn: 'root' })
export class LoadingService {
  private activeRequests = 0;
  private readonly _loading$ = new BehaviorSubject<boolean>(false);

  /**
   * True while one or more tracked requests are in flight. Debounced so
   * back-to-back calls don't flicker the overlay, and to sidestep
   * ExpressionChangedAfterItHasBeenChecked when a request starts during change
   * detection (e.g. from a component's ngOnInit).
   */
  readonly loading$: Observable<boolean> = this._loading$.pipe(
    debounceTime(10),
    distinctUntilChanged()
  );

  show(): void {
    this.activeRequests++;
    if (this.activeRequests === 1) {
      this._loading$.next(true);
    }
  }

  hide(): void {
    if (this.activeRequests === 0) {
      return;
    }
    this.activeRequests--;
    if (this.activeRequests === 0) {
      this._loading$.next(false);
    }
  }
}
